<script setup lang="ts">
  import { onBeforeUnmount, reactive, computed } from 'vue';
  import { type WMModal, type WMModalArea } from '../composables/WM';
  import Icon from './Icon.vue';

  const props = defineProps<{
    modal: WMModal,
    containerCssClass: string,
    titleCssClass: string,
    closeCssClass: string,
  }>();

  // Oblasti okna (icon / afterTitle / tools / footer) vznikají v WMModal.getArea() a vkládá do nich
  // vložená komponenta přes <teleport> (docs/tasks/055.md). Okno si hlídá, které z nich mají obsah —
  // prázdná oblast nezabírá místo a hlavička/patička se bez obsahu vůbec nevykreslí.
  const areas: WMModalArea[] = ['icon', 'afterTitle', 'tools', 'footer'];
  const hasContent = reactive<Record<WMModalArea, boolean>>({ icon: false, afterTitle: false, tools: false, footer: false });
  const observers = areas.map(area => {
    const el = props.modal.getArea(area);
    const update = () => { hasContent[area] = el.childElementCount > 0 || (el.textContent ?? '').trim() !== ''; };
    const observer = new MutationObserver(update);
    observer.observe(el, { childList: true, subtree: true, characterData: true });
    update();
    return observer;
  });
  onBeforeUnmount(() => observers.forEach(o => o.disconnect()));

  // Funkce-ref: zavolá se pokaždé, když se hostitel (znovu) vykreslí → oblast se do něj vždy zasadí.
  const attach = (area: WMModalArea) => (host: unknown) => {
    if (host instanceof HTMLElement) host.appendChild(props.modal.getArea(area));
  };

  const hasHeader = computed(() => !!props.modal.title || props.modal.isCloseable
    || hasContent.icon || hasContent.afterTitle || hasContent.tools);
</script>

<template>
  <div :class="[props.containerCssClass, 'wm-window']">
    <header v-if="hasHeader" class="wm-window__header">
      <div v-show="hasContent.icon" :ref="attach('icon')" class="wm-window__host wm-window__host--icon"></div>
      <div :class="props.titleCssClass" v-if="props.modal.title">{{ props.modal.title }}</div>
      <div v-show="hasContent.afterTitle" :ref="attach('afterTitle')" class="wm-window__host wm-window__host--afterTitle"></div>
      <div class="wm-window__spacer"></div>
      <div v-show="hasContent.tools" :ref="attach('tools')" class="wm-window__host wm-window__host--tools"></div>
      <Icon v-if="props.modal.isCloseable" icon="close" element="button" :css-class="props.closeCssClass" @click="modal.close(undefined)" />
    </header>
    <div class="wm-window__body">
      <component :is="props.modal.component" v-bind="{ ...modal.props, modal: modal}"></component>
    </div>
    <footer v-show="hasContent.footer" :ref="attach('footer')" class="wm-window__footer"></footer>
  </div>
</template>

<style scoped>

</style>
